import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ConsentCategory = 'essential' | 'functional' | 'analytics' | 'marketing';

export const CONSENT_VERSION = 1;

const initialCategories: Record<ConsentCategory, boolean> = {
  essential: true,
  functional: false,
  analytics: false,
  marketing: false,
};

interface ConsentState {
  decided: boolean;
  decidedAt: number | null;
  consentVersion: number;
  categories: Record<ConsentCategory, boolean>;
  preferencesOpener: (() => void) | null;
  setFromLibrary: (accepted: ConsentCategory[]) => void;
  registerPreferencesOpener: (fn: (() => void) | null) => void;
  openPreferences: () => void;
  has: (c: ConsentCategory) => boolean;
  reset: () => void;
}

export const useConsentStore = create<ConsentState>()(
  persist(
    (set, get) => ({
      decided: false,
      decidedAt: null,
      consentVersion: CONSENT_VERSION,
      categories: { ...initialCategories },
      preferencesOpener: null,
      setFromLibrary: (accepted) =>
        set({
          decided: true,
          decidedAt: Date.now(),
          consentVersion: CONSENT_VERSION,
          categories: {
            essential: true,
            functional: accepted.includes('functional'),
            analytics: accepted.includes('analytics'),
            marketing: accepted.includes('marketing'),
          },
        }),
      registerPreferencesOpener: (fn) => set({ preferencesOpener: fn }),
      openPreferences: () => get().preferencesOpener?.(),
      has: (c) => get().categories[c],
      reset: () =>
        set({
          decided: false,
          decidedAt: null,
          consentVersion: CONSENT_VERSION,
          categories: { ...initialCategories },
          preferencesOpener: null,
        }),
    }),
    {
      name: 'consentStore',
      // Never persist the runtime-only opener callback.
      partialize: (s) => ({
        decided: s.decided,
        decidedAt: s.decidedAt,
        consentVersion: s.consentVersion,
        categories: s.categories,
      }),
    },
  ),
);
